declare global {
  interface Window {
    dataLayer: Record<string, unknown>[]
  }
}

export interface GTMEventParams {
  event: string
  [key: string]: unknown
}

let photoUploadId: string = ''

function generatePhotoUploadId(): void {
  photoUploadId = `upload-${crypto.randomUUID()}`
}

function formatFileSize(bytes: number): string {
  const megabytes = bytes / (1024 * 1024)
  return `${megabytes.toFixed(2)} MB`
}

/**
 * Sends a custom event to Google Analytics via Google Tag Manager's dataLayer.
 *
 * @param eventName - The name of the event (e.g., 'button_click', 'form_submit')
 * @param params - Optional additional parameters to include with the event
 *
 * @example
 * // Simple event
 * sendAnalyticsEvent('signup_started')
 *
 * // Event with parameters
 * sendGTMEvent('contact_added', { contact_count: 5, method: 'manual' })
 */
export function sendGTMEvent(eventName: string, params?: Record<string, unknown>): void {
  if (typeof window === 'undefined' || !window.dataLayer) {
    console.warn('Google Tag Manager dataLayer not available')
    return
  }

  const eventData: GTMEventParams = {
    event: eventName,
    ...params,
  }

  window.dataLayer.push(eventData)
}

export interface UploadPhotoEventParams {
  isOnboarding: boolean
  isReplacing: boolean
}

/**
 * Sends an 'upload_photo' event when the user clicks the Upload Photo button.
 *
 * @param params.isOnboarding - Whether the user is in the onboarding flow
 * @param params.isReplacing - Whether the user is replacing an existing photo
 */
export function sendUploadPhotoEvent({ isOnboarding, isReplacing }: UploadPhotoEventParams): void {
  generatePhotoUploadId()
  sendGTMEvent('upload_photo', {
    photo_upload_id: photoUploadId,
    is_onboarding: isOnboarding,
    is_replacing: isReplacing,
  })
}

/**
 * Sends an 'upload_photo_file_selected' event when the user selects a file to upload.
 *
 * @param mimeType - The MIME type of the selected file
 */
export function sendUploadPhotoFileSelectedEvent(mimeType: string): void {
  sendGTMEvent('upload_photo_file_selected', {
    photo_upload_id: photoUploadId,
    mime_type: mimeType,
  })
}

export function sendUploadPhotoLargeSize(fileSize: number) {
  sendGTMEvent('upload_photo_large_size', {
    file_size_mb: formatFileSize(fileSize),
  })
}

/**
 * Sends an 'upload_photo_editor_closed' event when the user closes the editor without saving.
 */
export function sendUploadPhotoEditorClosedEvent(): void {
  sendGTMEvent('upload_photo_editor_closed', {
    photo_upload_id: photoUploadId,
  })
}

/**
 * Sends an 'upload_photo_success' event when a photo upload completes successfully.
 *
 * @param fileSize - The size of the uploaded file in bytes
 */
export function sendUploadPhotoSuccessEvent(fileSize: number): void {
  sendGTMEvent('upload_photo_success', {
    photo_upload_id: photoUploadId,
    file_size_mb: formatFileSize(fileSize),
  })
}

/**
 * Sends an 'upload_photo_error' event when a photo upload fails.
 *
 * @param fileSize - The size of the file that failed to upload in bytes
 */
export function sendUploadPhotoErrorEvent(fileSize: number): void {
  sendGTMEvent('upload_photo_error', {
    photo_upload_id: photoUploadId,
    file_size_mb: formatFileSize(fileSize),
  })
}

/**
 * Sends an 'upload_photo_deleted' event when the user deletes their photo.
 */
export function sendUploadPhotoDeletedEvent(): void {
  sendGTMEvent('upload_photo_deleted', {})
}

/**
 * Sends a 'language_detected' event when the initial language is set by the browser.
 *
 * @param language - The detected language code (e.g., 'en-US', 'es')
 */
export function sendLanguageDetectedEvent(language: string): void {
  sendGTMEvent('language_detected', {
    language,
  })
}

/**
 * Sends a 'language_changed' event when the user toggles the language.
 *
 * @param language - The newly selected language code (e.g., 'en-US', 'es')
 */
export function sendLanguageChangedEvent(language: string): void {
  sendGTMEvent('language_changed', {
    language,
  })
}

export interface StudentAddEventParams {
  isOnboarding: boolean
  studentCount: number
}

/**
 * Sends a 'student_added' event when a new student is added successfully.
 *
 * @param params.isOnboarding - Whether the user is in the onboarding flow
 * @param params.studentCount - The total number of students after the addition
 */
export function sendStudentAddedEvent({ isOnboarding, studentCount }: StudentAddEventParams): void {
  sendGTMEvent('student_add_success', {
    is_onboarding: isOnboarding,
    student_count: studentCount,
  })
}

export interface StudentAddErrorEventParams {
  isOnboarding: boolean
}

/**
 * Sends a 'student_add_error' event when adding a student fails.
 *
 * @param params.isOnboarding - Whether the user is in the onboarding flow
 */
export function sendStudentAddErrorEvent({ isOnboarding }: StudentAddErrorEventParams): void {
  sendGTMEvent('student_add_error', {
    is_onboarding: isOnboarding,
  })
}

export interface StudentEditEventParams {
  isOnboarding: boolean
}

/**
 * Sends a 'student_edited' event when a student is edited successfully.
 *
 * @param params.isOnboarding - Whether the user is in the onboarding flow
 */
export function sendStudentEditedEvent({ isOnboarding }: StudentEditEventParams): void {
  sendGTMEvent('student_edit_success', {
    is_onboarding: isOnboarding,
  })
}

/**
 * Sends a 'student_edit_error' event when editing a student fails.
 *
 * @param params.isOnboarding - Whether the user is in the onboarding flow
 */
export function sendStudentEditErrorEvent({ isOnboarding }: StudentEditEventParams): void {
  sendGTMEvent('student_edit_error', {
    is_onboarding: isOnboarding,
  })
}

export interface StudentDeleteEventParams {
  isOnboarding: boolean
  studentCount: number
}

/**
 * Sends a 'student_deleted' event when a student is deleted successfully.
 *
 * @param params.isOnboarding - Whether the user is in the onboarding flow
 * @param params.studentCount - The total number of students after the deletion
 */
export function sendStudentDeletedEvent({ isOnboarding, studentCount }: StudentDeleteEventParams): void {
  sendGTMEvent('student_delete_success', {
    is_onboarding: isOnboarding,
    student_count: studentCount,
  })
}

export interface StudentDeleteErrorEventParams {
  isOnboarding: boolean
}

/**
 * Sends a 'student_delete_error' event when deleting a student fails.
 *
 * @param params.isOnboarding - Whether the user is in the onboarding flow
 */
export function sendStudentDeleteErrorEvent({ isOnboarding }: StudentDeleteErrorEventParams): void {
  sendGTMEvent('student_delete_error', {
    is_onboarding: isOnboarding,
  })
}

export interface ContactAddEventParams {
  isOnboarding: boolean
  hasEmail: boolean
  hasPhone: boolean
  contactCount: number
}

/**
 * Sends a 'contact_add_success' event when a new contact is added successfully.
 *
 * @param params.isOnboarding - Whether the user is in the onboarding flow
 * @param params.hasEmail - Whether a valid email was provided
 * @param params.hasPhone - Whether a valid phone number was provided
 * @param params.contactCount - The total number of contacts after the addition
 */
export function sendContactAddedEvent({ isOnboarding, hasEmail, hasPhone, contactCount }: ContactAddEventParams): void {
  sendGTMEvent('contact_add_success', {
    is_onboarding: isOnboarding,
    outreach_method: getOutreachMethod(hasEmail, hasPhone),
    contact_count: contactCount,
  })
}

/**
 * Helper function to determine the outreach method based on email and phone presence.
 */
export function getOutreachMethod(hasEmail: boolean, hasPhone: boolean): 'email_only' | 'sms_only' | 'both' {
  if (hasEmail && hasPhone) return 'both'
  if (hasEmail) return 'email_only'
  return 'sms_only'
}

interface BulkUploadEventParams {
  isOnboarding: boolean
  source: string
  bulkUploadCount: number
  errorCount: number
  contactCount: number
}

/**
 * Sends a 'contact_bulk_upload_success' event when a new bulk upload completes
 *
 * @param params.isOnboarding - Whether the user is in the onboarding flow
 * @param params.source - The source of the upload: Gmail, Yahoo, or Outlook
 * @param params.bulkUploadCount - The number of contacts added successfully
 * @param params.errorCount - The number of contacts that failed to be uploaded
 * @param params.contactCount - The total number of contacts after the addition
 */
export function sendBulkContactUploadEvent({
  isOnboarding,
  source,
  bulkUploadCount,
  errorCount,
  contactCount,
}: BulkUploadEventParams): void {
  sendGTMEvent('contact_bulk_upload', {
    is_onboarding: isOnboarding,
    source,
    uploaded_contact_count: bulkUploadCount,
    error_count: errorCount,
    contact_count: contactCount,
  })
}

export interface ContactAddErrorEventParams {
  isOnboarding: boolean
}

/**
 * Sends a 'contact_add_error' event when adding a contact fails.
 *
 * @param params.isOnboarding - Whether the user is in the onboarding flow
 */
export function sendContactAddErrorEvent({ isOnboarding }: ContactAddErrorEventParams): void {
  sendGTMEvent('contact_add_error', {
    is_onboarding: isOnboarding,
  })
}

export interface ContactEditEventParams {
  isOnboarding: boolean
}

/**
 * Sends a 'contact_edit_success' event when a contact is edited successfully.
 *
 * @param params.isOnboarding - Whether the user is in the onboarding flow
 */
export function sendContactEditedEvent({ isOnboarding }: ContactEditEventParams): void {
  sendGTMEvent('contact_edit_success', {
    is_onboarding: isOnboarding,
  })
}

/**
 * Sends a 'contact_edit_error' event when editing a contact fails.
 *
 * @param params.isOnboarding - Whether the user is in the onboarding flow
 */
export function sendContactEditErrorEvent({ isOnboarding }: ContactEditEventParams): void {
  sendGTMEvent('contact_edit_error', {
    is_onboarding: isOnboarding,
  })
}

export interface ContactDeleteEventParams {
  isOnboarding: boolean
  contactCount: number
}

/**
 * Sends a 'contact_delete_success' event when a contact is deleted successfully.
 *
 * @param params.isOnboarding - Whether the user is in the onboarding flow
 * @param params.contactCount - The total number of contacts after the deletion
 */
export function sendContactDeletedEvent({ isOnboarding, contactCount }: ContactDeleteEventParams): void {
  sendGTMEvent('contact_delete_success', {
    is_onboarding: isOnboarding,
    contact_count: contactCount,
  })
}

export interface ContactDeleteErrorEventParams {
  isOnboarding: boolean
}

/**
 * Sends a 'contact_delete_error' event when deleting a contact fails.
 *
 * @param params.isOnboarding - Whether the user is in the onboarding flow
 */
export function sendContactDeleteErrorEvent({ isOnboarding }: ContactDeleteErrorEventParams): void {
  sendGTMEvent('contact_delete_error', {
    is_onboarding: isOnboarding,
  })
}

export interface ContactValidationErrorEventParams {
  field: 'email' | 'phone'
}

export interface InviteMoreClickedEventParams {
  source: string
}
export function sendInviteMoreClickedEvent(source: string) {
  sendGTMEvent('invite_more_button_clicked', {
    source,
  })
}

/**
 * Sends a 'request_magic_link' event when a user successfully requests a magic link.
 */
export function sendRequestMagicLinkEvent(): void {
  sendGTMEvent('request_magic_link')
}

/**
 * Sends a 'signin_password' event when a user successfully signs in with a password.
 */
export function sendSigninPasswordEvent(): void {
  sendGTMEvent('signin_password')
}

/**
 * Sends a 'register_password' event when a user successfully registers with a password.
 */
export function sendRegisterPasswordEvent(): void {
  sendGTMEvent('register_password')
}

/**
 * Sends a 'signin_magic_link' event when a user successfully signs in via a magic link token.
 */
export function sendSigninMagicLinkEvent(): void {
  sendGTMEvent('signin_magic_link')
}

export interface AuthErrorEventParams {
  action: 'request_magic_link' | 'signin_magic_link' | 'signin_password' | 'register_password'
  errorCause?: string
}

/**
 * Sends an 'auth_error' event when an authentication or registration attempt fails.
 *
 * @param params.action - The auth action that was being attempted
 * @param params.errorCause - Optional description of why the error occurred
 */
export function sendAuthErrorEvent({ action, errorCause }: AuthErrorEventParams): void {
  sendGTMEvent('auth_error', {
    action,
    ...(errorCause && { error_cause: errorCause }),
  })
}

/**
 * Sends a 'disney_tos_clicked' event when a user opens the Disney raffle
 * terms page from the prize progress bar.
 *
 * @param inviteCount - The user's invite count when they clicked
 */
export function sendDisneyTosClickedEvent(inviteCount: number): void {
  sendGTMEvent('disney_tos_clicked', {
    invite_count: inviteCount,
  })
}

/**
 * Sends a 'video_start' event the first time a video is played.
 *
 * @param videoId - Identifier for the video (e.g. 'parent_explainer')
 */
export function sendVideoStartEvent(videoId: string): void {
  sendGTMEvent('video_start', {
    video_id: videoId,
  })
}

/**
 * Sends a 'video_progress' event when playback passes a milestone.
 *
 * @param videoId - Identifier for the video (e.g. 'parent_explainer')
 * @param percent - The milestone reached (25, 50, or 75)
 */
export function sendVideoProgressEvent(videoId: string, percent: number): void {
  sendGTMEvent('video_progress', {
    video_id: videoId,
    video_percent: percent,
  })
}

/**
 * Sends a 'video_complete' event when a video plays through to the end.
 *
 * @param videoId - Identifier for the video (e.g. 'parent_explainer')
 */
export function sendVideoCompleteEvent(videoId: string): void {
  sendGTMEvent('video_complete', {
    video_id: videoId,
  })
}
